import { Separator } from '@/components/atoms/separator';
import { FeedbackPrompt } from '@/components/molecules/FeedbackPrompt';
import { MetricForecast } from '@/components/molecules/MetricForecast';
import { ACTION_TRACKED } from '@/libs/mixpanel';
import { STORAGE_KEYS } from '@/libs/storage';
import { convertYearlyKWhToSessionsPerDay, formatNumber } from '@/libs/utils/format';
import type { IForecast } from '@/types';

interface ForecastedMetricsProps {
  forecast: IForecast;
  /** Number of features to display (default: all) */
  limit?: number;
  /** Number of features to skip from the beginning */
  offset?: number;
  /** Hide the main forecast value and header */
  hideHeader?: boolean;
  /** Project ID for feedback tracking */
  projectId: string;
}

export function ForecastedMetrics({
  forecast,
  limit,
  offset = 0,
  hideHeader = false,
  projectId,
}: ForecastedMetricsProps) {
  // Sort features by importance (1 being highest priority, so ascending order)
  const sortedFeatures = Object.entries(forecast.features).sort(
    ([, a], [, b]) => a.importance - b.importance,
  );

  // Apply offset and limit
  const displayedFeatures = sortedFeatures.slice(offset, limit ? offset + limit : undefined);

  // A forecast saved before the worker stored sessions/day carries the column default, 0: keep
  // the former 32 kWh estimate for it rather than showing no sessions for a site with demand.
  const sessionsPerDay =
    forecast.sessions_per_day === 0 && forecast.value > 0
      ? convertYearlyKWhToSessionsPerDay(forecast.value)
      : forecast.sessions_per_day;

  return (
    <div>
      {!hideHeader && (
        <>
          <MetricForecast
            variant="main"
            label="Estimated yearly demand"
            value={forecast.value}
            unit={forecast.unit}
            secondaryValue={sessionsPerDay}
            secondaryUnit="sessions per day"
            formatValue={(v) => formatNumber(v, 0)}
            formatSecondaryValue={(v) => formatNumber(v, 2)}
          />
          <FeedbackPrompt
            text="Does this forecast match your expectation?"
            eventName={ACTION_TRACKED.FEEDBACK_FORECAST}
            projectId={projectId}
            storageKey={STORAGE_KEYS.FEEDBACK_FORECAST}
            className="mt-2"
          />
          <Separator className="mt-3 mb-1.5" />
          <div className="font-bold text-sm mb-2">Top demand factors</div>
        </>
      )}
      {displayedFeatures.map(([key, feature], index) => (
        <div key={key}>
          <MetricForecast
            label={feature.name || key}
            value={feature.value}
            unit={feature.unit}
            secondaryValue={feature.shap}
            secondaryUnit={forecast.unit}
            totalValue={forecast.value}
            formatValue={formatNumber}
            showMetricBar
          />
          {index < displayedFeatures.length - 1 && <Separator className="mt-3 mb-1.5" />}
        </div>
      ))}
    </div>
  );
}
